import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import ApiErrorBanner from './ApiErrorBanner';
import { DataDelayNotice } from './DataDelayNotice';
import TickerSearch from './TickerSearch';

interface TopTicker {
  ticker: string;
  mentions: number;
  avg_sentiment: number;
  bullish_predictions: number;
  bearish_predictions: number;
  neutral_predictions: number;
}

const HOUR_OPTIONS = [6, 12, 24] as const;

const TopTickers: React.FC = () => {
  const navigate = useNavigate();
  const { isPro, loading: authLoading } = useAuth();
  const [topTickers, setTopTickers] = useState<TopTicker[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState<'mentions' | 'sentiment'>('mentions');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedHours, setSelectedHours] = useState<number>(6);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // FREE: макс 12h; PRO: до 24h и выше
  const canSelectHours = (hours: number) => isPro || hours <= 12;

  // Сброс 24h при переходе на FREE
  useEffect(() => {
    if (!isPro && selectedHours > 12) {
      setSelectedHours(12);
    }
  }, [isPro, selectedHours]);
    
  const fetchData = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
    }
    setError(null);

    try {
      // Используем только серверную сортировку
      const response = await apiService.getTopTickers(selectedHours, 9, sortBy, sortOrder, isPro);

      if (response && response.tickers && response.tickers.length > 0) {
        // Преобразуем формат ответа: используем positive/negative/neutralMentions из API
        const formattedTickers = response.tickers.map(t => ({
          ticker: t.ticker,
          mentions: t.mentions,
          avg_sentiment: t.avgSentiment ?? 0,
          bullish_predictions: t.positiveMentions ?? 0,
          bearish_predictions: t.negativeMentions ?? 0,
          neutral_predictions: t.neutralMentions ?? 0,
        }));
        setTopTickers(formattedTickers);
        setError(null);
        setLastUpdate(new Date());
      } else {
        setError('No data available for the selected period');
        setTopTickers([]);
      }
    } catch (error) {
      console.error('TopTickers: Error loading top tickers:', error);
      if (error instanceof Error) {
        if (error.message.includes('404') || error.message.includes('not found')) {
          setError('Tickers not found');
        } else if (error.message.includes('429')) {
          setError('Too many requests. Please try again later.');
        } else {
          setError('Error loading data. Please try again later.');
        }
      } else {
        setError('Error loading data.');
      }
      setTopTickers([]);
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  }, [selectedHours, sortBy, sortOrder, isPro]);
    
  useEffect(() => {
    if (authLoading) return;
    fetchData(true);

    const interval = setInterval(() => fetchData(false), 5 * 60 * 1000);
    return () => {
      clearInterval(interval);
    };
  }, [fetchData, authLoading]);

  // Используем только серверную сортировку - данные уже отсортированы на сервере
  const sortedTickers = topTickers;

  const handleTickerClick = (ticker: string) => {
    navigate(`/ticker/${ticker}`);
  };



  const getSentimentColor = (sentiment: number) => {
    if (sentiment >= 0.2) return 'text-brand-cyan';
    if (sentiment <= -0.2) return 'text-brand-red';
    return 'text-brand-yellow';
  };

  const getSentimentIcon = (sentiment: number) => {
    if (sentiment >= 0.2) return <TrendingUp className="w-4 h-4 text-brand-cyan" />;
    if (sentiment <= -0.2) return <TrendingDown className="w-4 h-4 text-brand-red" />;
    return <Minus className="w-4 h-4 text-brand-yellow" />;
  };

  const getSentimentBadge = (sentiment: number) => {
    if (sentiment >= 0.2) return <span className="px-2 py-1 bg-brand-cyan/10 text-brand-cyan text-xs rounded-full border border-brand-cyan/20">Positive</span>;
    if (sentiment <= -0.2) return <span className="px-2 py-1 bg-brand-red/10 text-brand-red text-xs rounded-full border border-brand-red/20">Negative</span>;
    return <span className="px-2 py-1 bg-brand-yellow/10 text-brand-yellow text-xs rounded-full border border-brand-yellow/20">Neutral</span>;
  };
  
  return (
    <div className="space-y-6">
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-cyan"></div>
          <span className="ml-2 text-[#bbb]">Loading tickers...</span>
        </div>
      )}
      {error && <ApiErrorBanner message={error} />}
      <DataDelayNotice />
      
      {/* Ticker Search Section — золотой стиль SentiHype */}
      <div
        className="top-tickers-search-wrap px-6 py-6 mb-6 rounded-2xl overflow-hidden"
      >
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-brand-gold mb-1">Find Any Ticker</h2>
          <p className="text-sm text-white/60">Search for any ticker to view its sentiment analysis</p>
        </div>
        <TickerSearch />
      </div>

      {/* Заголовок */}
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-brand-gold mb-1">Top Trending Tickers</h2>
        <p className="text-sm text-white/60">
          Last {selectedHours} {selectedHours === 1 ? 'hour' : 'hours'} • {sortedTickers.length} tickers
          {lastUpdate && (
            <span className="ml-2">
              • Updated: {lastUpdate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </p>
      </div>

      {/* Time range, Sort by, Order — на мобильном столбиком (как на странице тикера), на sm+ одна строка */}
      <div
        className="top-tickers-filters-wrap dashboard-filters min-h-0 flex flex-col sm:flex-row sm:flex-nowrap sm:items-center gap-4 sm:gap-6 mb-6 w-full max-w-full rounded-xl border overflow-x-visible sm:overflow-x-auto overflow-y-hidden px-4 sm:px-5 py-4"
      >
        {/* Time range (бывший Period) */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0 w-full sm:w-auto sm:shrink-0">
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs font-medium uppercase tracking-wider text-brand-gold/90">Time range</span>
            <span className="text-xs text-white/50">{isPro ? '(up to 24h)' : '(up to 12h)'}</span>
          </div>
          <div className="top-tickers-pill-group scroll-x-only min-h-0 flex rounded-lg overflow-hidden border bg-black/30 overflow-x-auto overflow-y-hidden shrink-0">
            {HOUR_OPTIONS.map((hours) => {
              const enabled = canSelectHours(hours);
              const isActive = selectedHours === hours;
              return (
                <button
                  key={hours}
                  type="button"
                  disabled={!enabled}
                  onClick={() => enabled && setSelectedHours(hours)}
                  title={!enabled ? 'Premium only — 24h' : undefined}
                  className={`top-tickers-pill-btn px-3 sm:px-4 py-2 text-sm font-medium transition-colors border-r last:border-r-0 shrink-0 ${
                    !enabled
                      ? 'text-white/30 cursor-not-allowed bg-white/5 border-white/10 pointer-events-none'
                      : isActive
                        ? 'top-tickers-pill-btn-active text-black font-bold border-[#D4AF37]/30'
                        : 'text-white/80 hover:bg-white/10 hover:text-white border-white/10'
                  }`}
                >
                  {hours}h
                </button>
              );
            })}
          </div>
        </div>
        {/* Sort by */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0 w-full sm:w-auto sm:shrink-0">
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs font-medium uppercase tracking-wider text-brand-gold/90">Sort by</span>
          </div>
          <div className="top-tickers-pill-group scroll-x-only min-h-0 flex rounded-lg overflow-hidden border bg-black/30 overflow-x-auto overflow-y-hidden shrink-0">
            {(['mentions', 'sentiment'] as const).map((value) => {
              const isActive = sortBy === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setSortBy(value)}
                  className={`top-tickers-pill-btn px-3 sm:px-4 py-2 text-sm font-medium transition-colors border-r last:border-r-0 shrink-0 capitalize ${
                    isActive
                      ? 'top-tickers-pill-btn-active text-black font-bold border-[#D4AF37]/30'
                      : 'text-white/80 hover:bg-white/10 hover:text-white border-white/10'
                  }`}
                >
                  {value}
                </button>
              );
            })}
          </div>
        </div>
        {/* Order */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0 w-full sm:w-auto sm:shrink-0">
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs font-medium uppercase tracking-wider text-brand-gold/90">Order</span>
          </div>
          <div className="top-tickers-pill-group scroll-x-only min-h-0 flex rounded-lg overflow-hidden border bg-black/30 overflow-x-auto overflow-y-hidden shrink-0">
            {(['desc', 'asc'] as const).map((value) => {
              const isActive = sortOrder === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setSortOrder(value)}
                  className={`top-tickers-pill-btn px-3 sm:px-4 py-2 text-sm font-medium transition-colors border-r last:border-r-0 shrink-0 ${
                    isActive
                      ? 'top-tickers-pill-btn-active text-black font-bold border-[#D4AF37]/30'
                      : 'text-white/80 hover:bg-white/10 hover:text-white border-white/10'
                  }`}
                >
                  {value === 'desc' ? 'Desc' : 'Asc'}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {/* Top Tickers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedTickers.map((ticker, index) => {
          // Для прогресс-бара: проценты из API или расчёт по количествам
          const totalPred = ticker.bullish_predictions + ticker.bearish_predictions + ticker.neutral_predictions;
          const bullishPercent = totalPred ? (ticker.bullish_predictions / totalPred) * 100 : 0;
          const bearishPercent = totalPred ? (ticker.bearish_predictions / totalPred) * 100 : 0;
          const neutralPercent = totalPred ? (ticker.neutral_predictions / totalPred) * 100 : 0;
          const hasBreakdown = totalPred > 0;

          return (
            <motion.div
              key={ticker.ticker}
              className="card-glass p-6 cursor-pointer"
              whileHover={{ y: -2, scale: 1.02 }}
              onClick={() => handleTickerClick(ticker.ticker)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white">${ticker.ticker}</h3>
                {getSentimentIcon(ticker.avg_sentiment)}
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-[#bbb] text-sm">Mentions</span>
                  <span className="font-semibold text-white">{ticker.mentions}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#bbb] text-sm">Sentiment</span>
                  <div className="flex items-center space-x-2">
                    <span className={`font-semibold ${getSentimentColor(ticker.avg_sentiment)}`}>{Math.round(ticker.avg_sentiment * 100)}%</span>
                    {getSentimentBadge(ticker.avg_sentiment)}
                  </div>
                </div>
                <div className="w-full bg-[#333] rounded-full h-2">
                  <motion.div
                    className={`h-2 rounded-full ${
                      ticker.avg_sentiment >= 0.2 ? 'bg-brand-cyan' : 
                      ticker.avg_sentiment <= -0.2 ? 'bg-brand-red' : 'bg-brand-yellow'
                    }`}
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.abs(ticker.avg_sentiment) * 100}%` }}
                    transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                  />
                </div>
                {/* Predictions Summary — показываем только если бэкенд вернул разбивку */}
                {hasBreakdown && (
                  <>
                    <div className="top-tickers-breakdown-divider pt-2 border-t">
                      <div className="flex justify-between text-xs">
                        <span className="text-brand-cyan font-semibold">Bullish: {ticker.bullish_predictions}</span>
                        <span className="text-brand-red font-semibold">Bearish: {ticker.bearish_predictions}</span>
                        <span className="text-brand-yellow font-semibold">Neutral: {ticker.neutral_predictions}</span>
                      </div>
                    </div>
                    <div className="w-full h-2 mt-2 flex rounded overflow-hidden">
                      <div style={{ width: `${bullishPercent}%` }} className="bg-brand-cyan h-2" />
                      <div style={{ width: `${bearishPercent}%` }} className="bg-brand-red h-2" />
                      <div style={{ width: `${neutralPercent}%` }} className="bg-brand-yellow h-2" />
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default TopTickers; 
